<template>
  <div class="theme-toggle" role="group" :aria-label="$t('Theme.Label')">

    <!-- Десктоп: три отдельные кнопки -->
    <button
      v-for="opt in options"
      :key="opt.mode"
      class="theme-toggle__btn d-none d-lg-flex"
      :class="{ 'theme-toggle__btn--active': currentMode === opt.mode }"
      :title="opt.label"
      @click="setMode(opt.mode)"
    >
      <i :class="opt.icon"></i>
    </button>

    <!-- Мобилка: одна кнопка с циклом -->
    <div class="theme-toggle-wrap d-flex d-lg-none">
      <button
        class="theme-toggle__btn theme-toggle__btn--cycle"
        :aria-label="$t('Theme.Toggle')"
        @click="cycleMode"
      >
        <transition name="icon-spin" mode="out-in">
          <span :key="currentMode" class="theme-icon"><i :class="currentOption.icon"></i></span>
        </transition>
      </button>

      <transition name="tooltip-fade">
        <span v-if="showTooltip" class="theme-toggle__tooltip">
          {{ currentOption.label }}
        </span>
      </transition>
    </div>

  </div>
</template>

<script>
export default {
  name: 'ThemeToggle',
  data() {
    return {
      showTooltip: false,
      tooltipTimer: null,
    }
  },
  computed: {
    currentMode() {
      return this.$store.state.theme.themeMode
    },
    options() {
      return [
        { mode: 'light', icon: 'fas fa-sun',                  label: this.$t('Theme.Light') },
        { mode: 'auto',  icon: 'fas fa-circle-half-stroke',   label: this.$t('Theme.Auto')  },
        { mode: 'dark',  icon: 'fas fa-moon',                 label: this.$t('Theme.Dark')  },
      ]
    },
    currentOption() {
      return this.options.find(o => o.mode === this.currentMode) || this.options[0]
    },
  },
  methods: {
    setMode(mode) {
      this.$store.commit('theme/setThemeMode', mode)
    },
    cycleMode() {
      const idx = this.options.findIndex(o => o.mode === this.currentMode)
      const next = this.options[(idx + 1) % this.options.length]
      this.setMode(next.mode)
      clearTimeout(this.tooltipTimer)
      this.showTooltip = true
      this.tooltipTimer = setTimeout(() => { this.showTooltip = false }, 1600)
    },
  },
  beforeUnmount() {
    clearTimeout(this.tooltipTimer)
  },
}
</script>

<style scoped>
.theme-toggle {
  display: flex;
  align-items: center;
  background: var(--navbar-pill-bg, rgba(0,0,0,0.05));
  border: 1px solid var(--navbar-pill-border, rgba(0,0,0,0.08));
  border-radius: 8px;
  padding: 2px;
  gap: 1px;
}

.theme-toggle__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 26px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--navbar-text-muted, #6b7280);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  font-size: 12px;
  padding: 0;
  line-height: 1;
}

.theme-toggle__btn:hover {
  background: rgba(0,0,0,0.06);
  color: var(--navbar-text, #374151);
}

.theme-toggle__btn--active {
  background: var(--ui-bg-card, #fff);
  color: var(--sidebar-active-color, #3438b1);
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);
}

/* Мобильная кнопка чуть шире чтобы было удобно тапать */
.theme-toggle__btn--cycle {
  width: 32px;
  height: 28px;
  transition: background 0.15s ease, color 0.15s ease, transform 0.12s ease;
}
.theme-toggle__btn--cycle:hover  { transform: scale(1.06); }
.theme-toggle__btn--cycle:active { transform: scale(0.93); }

/* Тёмная тема */
body.dark-version .theme-toggle__btn:hover {
  background: rgba(255,255,255,0.08);
  color: var(--ui-text-1);
}

body.dark-version .theme-toggle__btn--active {
  background: rgba(255,255,255,0.10);
  color: var(--sidebar-active-color, #8B8FEC);
  box-shadow: 0 1px 3px rgba(0,0,0,0.4);
}

/* ── иконка: spin при смене ── */
.icon-spin-enter-active { animation: icon-in 0.22s ease; }
.icon-spin-leave-active { animation: icon-out 0.15s ease; }
@keyframes icon-in  { from { opacity:0; transform:rotate(-60deg) scale(0.6); } to { opacity:1; transform:rotate(0deg) scale(1); } }
@keyframes icon-out { from { opacity:1; transform:rotate(0deg) scale(1); }   to { opacity:0; transform:rotate(60deg) scale(0.6); } }

.theme-icon { display: flex; align-items: center; justify-content: center; }

/* ── обёртка мобильной кнопки ── */
.theme-toggle-wrap {
  position: relative;
  align-items: center;
}

/* ── подсказка ── */
.theme-toggle__tooltip {
  position: absolute;
  top: calc(100% + 7px);
  left: 50%;
  transform: translateX(-50%);
  background: var(--ui-bg-card, #fff);
  color: var(--ui-text-1, #111827);
  border: 1px solid var(--navbar-pill-border, rgba(0,0,0,0.1));
  border-radius: 6px;
  padding: 3px 9px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 3px 10px rgba(0,0,0,0.12);
  z-index: 999;
}
.theme-toggle__tooltip::after {
  content: '';
  position: absolute;
  bottom: 100%; left: 50%; transform: translateX(-50%);
  border: 5px solid transparent;
  border-bottom-color: var(--navbar-pill-border, rgba(0,0,0,0.1));
}
.theme-toggle__tooltip::before {
  content: '';
  position: absolute;
  bottom: 100%; left: 50%; transform: translateX(-50%);
  margin-bottom: -1px;
  border: 5px solid transparent;
  border-bottom-color: var(--ui-bg-card, #fff);
  z-index: 1;
}

body.dark-version .theme-toggle__tooltip {
  background: var(--ui-bg-card, #1e2433);
  color: var(--ui-text-1, #e5e7eb);
  border-color: rgba(255,255,255,0.12);
  box-shadow: 0 3px 12px rgba(0,0,0,0.4);
}
body.dark-version .theme-toggle__tooltip::after  { border-bottom-color: rgba(255,255,255,0.12); }
body.dark-version .theme-toggle__tooltip::before { border-bottom-color: var(--ui-bg-card, #1e2433); }

/* ── анимация подсказки ── */
.tooltip-fade-enter-active { animation: tip-pop 0.2s cubic-bezier(0.34,1.56,0.64,1); }
.tooltip-fade-leave-active { animation: tip-out 0.35s ease forwards; }
@keyframes tip-pop { from { opacity:0; transform:translateX(-50%) translateY(-6px) scale(0.85); } to { opacity:1; transform:translateX(-50%) translateY(0) scale(1); } }
@keyframes tip-out { 0% { opacity:1; transform:translateX(-50%) translateY(0) scale(1); } 70% { opacity:0.4; } 100% { opacity:0; transform:translateX(-50%) translateY(4px) scale(0.9); } }
</style>
